import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, MessageSquareIcon, PhoneIcon, CalendarIcon, DatabaseIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ArrowRightIcon, SmartphoneIcon, GlobeIcon, MailIcon, ChevronDownIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
export function AutomationsPage() {
  const faqs = [{
    question: 'How quickly does the AI respond to leads?',
    answer: 'Our AI systems respond to text messages, Yelp inquiries, and web forms within seconds, 24/7. For missed calls, the AI text-back feature triggers immediately after the call ends.'
  }, {
    question: 'Will it sound like a robot?',
    answer: 'No. We train the AI on your specific business information, services, and tone of voice. It sounds natural, helpful, and professional—most customers never realize they are speaking with AI.'
  }, {
    question: 'Can it actually book appointments?',
    answer: 'Yes. The AI integrates directly with your calendar. It can qualify the lead, answer basic questions, and provide a booking link or directly schedule the appointment based on your real-time availability.'
  }, {
    question: 'What happens if the AI does not know the answer?',
    answer: "If a customer asks a complex question outside the AI's training, it gracefully transitions the conversation. It will notify your team immediately and let the customer know a human will follow up shortly."
  }, {
    question: 'Do I need to change my current phone number?',
    answer: 'No. We can port your existing number into our system, or provide a new tracking number that forwards to your main line while enabling all the AI text-back features.'
  }];
  return <div className="pt-24 pb-16" data-id="element-469">
      <SEOHead title="AI Lead Conversion Services | Viridia Analytics" description="Turn more inquiries into booked jobs with instant AI responses. Speed-to-lead automation, AI receptionists, and Yelp chatbots for local service businesses." canonical="/ai-lead-conversion" keywords="AI lead conversion, speed to lead, missed call text back, AI receptionist, Yelp chatbot, lead nurturing automation, Phoenix local business leads" faq={faqs} data-id="element-470" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-471">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-viridia/10 via-viridia-black to-viridia-black -z-10" data-id="element-472"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-473">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-474">
            <ScrollReveal data-id="element-475">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-476">
                <ZapIcon className="w-4 h-4" data-id="element-477" />
                <span data-id="element-478">Speed-to-Lead Automation</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-479">
                Stop Losing Leads to{' '}
                <span className="text-viridia" data-id="element-480">Faster Competitors</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-481">
                When a customer needs a service, they hire the first business
                that responds. Our AI Lead Conversion system answers Yelp
                messages, missed calls, and web forms in seconds—turning
                inquiries into booked jobs 24/7.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-482">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-483">
                  Book a Strategy Call
                </Link>
                <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-surface border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-484">
                  View Pricing
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-485">
              <div className="relative" data-id="element-486">
                {/* Abstract visualization of leads flowing into booked jobs */}
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-487">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-viridia/5 rounded-full blur-3xl" data-id="element-488"></div>

                  <div className="flex flex-col gap-6 relative z-10" data-id="element-489">
                    {/* Incoming Leads */}
                    <div className="flex justify-between items-center gap-4" data-id="element-490">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.5
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-491">
                        <div className="w-10 h-10 rounded-full bg-[#FF1A1A]/10 flex items-center justify-center flex-shrink-0" data-id="element-492">
                          <MessageSquareIcon className="w-5 h-5 text-[#FF1A1A]" data-id="element-493" />
                        </div>
                        <div data-id="element-494">
                          <div className="text-xs text-gray-500" data-id="element-495">
                            Yelp Request
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-496">
                            "Need a quote for..."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.7
                    }} className="flex-1 bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-center gap-3" data-id="element-497">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0" data-id="element-498">
                          <PhoneIcon className="w-5 h-5 text-blue-500" data-id="element-499" />
                        </div>
                        <div data-id="element-500">
                          <div className="text-xs text-gray-500" data-id="element-501">
                            Missed Call
                          </div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-502">
                            From (602) 555-...
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* AI Processing */}
                    <div className="flex justify-center py-2" data-id="element-503">
                      <motion.div animate={{
                      y: [0, 5, 0]
                    }} transition={{
                      duration: 2,
                      repeat: Infinity
                    }} className="w-12 h-12 rounded-full bg-viridia/20 border border-viridia/50 flex items-center justify-center" data-id="element-504">
                        <ZapIcon className="w-6 h-6 text-viridia" data-id="element-505" />
                      </motion.div>
                    </div>

                    {/* Instant Responses */}
                    <div className="space-y-3" data-id="element-506">
                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.2
                    }} className="bg-viridia/5 border border-viridia/20 rounded-xl p-4 flex items-start gap-3 ml-8" data-id="element-507">
                        <div className="w-8 h-8 rounded-full bg-viridia flex items-center justify-center flex-shrink-0 mt-1" data-id="element-508">
                          <ZapIcon className="w-4 h-4 text-white" data-id="element-509" />
                        </div>
                        <div data-id="element-510">
                          <div className="text-xs text-viridia font-medium mb-1" data-id="element-511">
                            Instant AI Response (0.5s)
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-512">
                            "Hi! We can definitely help with that. Are you
                            looking to get this done this week?"
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 1.8
                    }} className="bg-viridia-black border border-gray-800 rounded-xl p-4 flex items-start gap-3 mr-8" data-id="element-513">
                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-514">
                          <SmartphoneIcon className="w-4 h-4 text-gray-400" data-id="element-515" />
                        </div>
                        <div data-id="element-516">
                          <div className="text-xs text-gray-500 mb-1" data-id="element-517">
                            Customer
                          </div>
                          <div className="text-sm text-gray-300" data-id="element-518">
                            "Yes, ideally Thursday."
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 2.4
                    }} className="bg-viridia/10 border border-viridia/30 rounded-xl p-4 flex items-center justify-between ml-8" data-id="element-519">
                        <div className="flex items-center gap-3" data-id="element-520">
                          <div className="w-10 h-10 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0" data-id="element-521">
                            <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-522" />
                          </div>
                          <div data-id="element-523">
                            <div className="text-sm font-bold text-white" data-id="element-524">
                              Appointment Booked
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-525">
                              Thursday @ 10:00 AM
                            </div>
                          </div>
                        </div>
                        <CheckCircleIcon className="w-6 h-6 text-viridia" data-id="element-526" />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Speed to Lead Comparison */}
      <section className="py-20 bg-viridia-black" data-id="element-527">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-528">
          <ScrollReveal data-id="element-529">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-530">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-531">
                The Cost of a Slow Response
              </h2>
              <p className="text-gray-400" data-id="element-532">
                In local services, the first to reply wins the job 78% of the
                time. If you're on a roof, under a sink, or driving, you're
                losing money to competitors who answer instantly.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-533">
            <ScrollReveal data-id="element-534">
              <div className="bg-viridia-surface border border-red-900/30 rounded-2xl p-8 relative overflow-hidden" data-id="element-535">
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl" data-id="element-536"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-537">
                  <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center" data-id="element-538">
                    <ClockIcon className="w-6 h-6 text-red-500" data-id="element-539" />
                  </div>
                  <div data-id="element-540">
                    <h3 className="text-xl font-bold text-white" data-id="element-541">
                      The Old Way
                    </h3>
                    <p className="text-red-400 text-sm" data-id="element-542">
                      Average response: 2-4 hours
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-543">
                  <li className="flex items-start gap-3" data-id="element-544">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-545" />
                    <span className="text-gray-300" data-id="element-546">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-547">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-548" />
                    <span className="text-gray-300" data-id="element-549">
                      Customer immediately calls the next business on
                      Yelp/Google
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-550">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-551" />
                    <span className="text-gray-300" data-id="element-552">
                      You call back 2 hours later
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-553">
                    <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-554" />
                    <span className="text-gray-300" data-id="element-555">
                      "Sorry, we already found someone."
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-red-500/10 rounded-lg border border-red-500/20" data-id="element-556">
                  <p className="text-red-400 font-medium text-center" data-id="element-557">
                    Result: Wasted ad spend & lost revenue
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-558">
              <div className="bg-viridia-surface border border-viridia/30 rounded-2xl p-8 relative overflow-hidden" data-id="element-559">
                <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-3xl" data-id="element-560"></div>
                <div className="flex items-center gap-4 mb-6" data-id="element-561">
                  <div className="w-12 h-12 rounded-full bg-viridia/20 flex items-center justify-center" data-id="element-562">
                    <ZapIcon className="w-6 h-6 text-viridia" data-id="element-563" />
                  </div>
                  <div data-id="element-564">
                    <h3 className="text-xl font-bold text-white" data-id="element-565">
                      The Viridia Way
                    </h3>
                    <p className="text-viridia text-sm" data-id="element-566">
                      Average response: &lt; 5 seconds
                    </p>
                  </div>
                </div>
                <ul className="space-y-4" data-id="element-567">
                  <li className="flex items-start gap-3" data-id="element-568">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-569" />
                    <span className="text-gray-300" data-id="element-570">
                      Miss a call while on a job site
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-571">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-572" />
                    <span className="text-gray-300" data-id="element-573">
                      AI instantly texts: "Hi, sorry we missed you! How can we
                      help?"
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-574">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-575" />
                    <span className="text-gray-300" data-id="element-576">
                      Customer engages with AI instead of calling competitors
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-577">
                    <CheckCircleIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-578" />
                    <span className="text-gray-300" data-id="element-579">
                      AI qualifies the lead and books the appointment
                    </span>
                  </li>
                </ul>
                <div className="mt-8 p-4 bg-viridia/10 rounded-lg border border-viridia/20" data-id="element-580">
                  <p className="text-viridia font-medium text-center" data-id="element-581">
                    Result: Captured lead & booked job
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* How It Works - 4 Steps */}
      <section className="py-20 bg-viridia-surface" data-id="element-582">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-583">
          <ScrollReveal data-id="element-584">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-585">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-586">
                How the System Works
              </h2>
              <p className="text-gray-400" data-id="element-587">
                A seamless, automated pipeline that catches every opportunity
                and drives it toward a booked appointment.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-588">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gray-800 -translate-y-1/2 z-0" data-id="element-589"></div>

            {[{
            step: '01',
            title: 'Lead Enters',
            desc: 'A customer calls, texts, fills out a form, or messages you on Yelp/Google.',
            icon: GlobeIcon
          }, {
            step: '02',
            title: 'Instant Response',
            desc: 'Within seconds, our AI acknowledges the inquiry and starts a conversation.',
            icon: ZapIcon
          }, {
            step: '03',
            title: 'Qualify & Nurture',
            desc: 'The AI asks qualifying questions to ensure they are a good fit for your services.',
            icon: MessageSquareIcon
          }, {
            step: '04',
            title: 'Book & Track',
            desc: 'The lead is booked on your calendar and tracked in your CRM pipeline.',
            icon: DatabaseIcon
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-590">
                <div className="relative z-10 bg-viridia-black border border-gray-800 rounded-2xl p-6 h-full flex flex-col" data-id="element-591">
                  <div className="w-12 h-12 rounded-full bg-viridia-surface border border-gray-700 flex items-center justify-center mb-6 mx-auto" data-id="element-592">
                    <item.icon className="w-5 h-5 text-viridia" data-id="element-593" />
                  </div>
                  <div className="text-center flex-grow" data-id="element-594">
                    <div className="text-xs font-bold text-viridia mb-2" data-id="element-595">
                      STEP {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3" data-id="element-596">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-597">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Service Blocks */}
      <section className="py-20 bg-viridia-black" data-id="element-598">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-599">
          <ScrollReveal data-id="element-600">
            <div className="mb-16" data-id="element-601">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-602">
                Core Conversion Features
              </h2>
              <p className="text-gray-400 max-w-2xl" data-id="element-603">
                Everything you need to ensure no lead falls through the cracks.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-604">
            {[{
            title: 'Missed Call Text-Back',
            desc: 'Never lose a lead to a missed call. The system instantly texts callers back: "Hi, sorry we missed your call! How can we help you today?"',
            icon: PhoneIcon
          }, {
            title: 'Yelp & Google AI Chatbot',
            desc: 'Instantly reply to messages on Yelp and Google Business Profile. The AI answers FAQs and pushes for the booking while competitors are still typing.',
            icon: MessageSquareIcon
          }, {
            title: 'Automated Lead Nurturing',
            desc: "Long-term drip campaigns via email and SMS to keep your business top-of-mind for leads who aren't ready to buy today.",
            icon: MailIcon
          }, {
            title: 'Calendar Integration',
            desc: 'The AI reads your real-time availability and books appointments directly onto your calendar without double-booking.',
            icon: CalendarIcon
          }, {
            title: 'CRM Pipeline Sync',
            desc: 'Every conversation, lead detail, and booked job is automatically logged in your CRM. Total visibility into your sales pipeline.',
            icon: DatabaseIcon
          }, {
            title: 'Human Takeover',
            desc: 'Monitor all AI conversations from one inbox. Jump in and take over the chat manually at any time with a single click.',
            icon: SmartphoneIcon
          }].map((feature, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-605">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 hover:border-viridia/50 transition-colors h-full" data-id="element-606">
                  <div className="w-12 h-12 rounded-lg bg-viridia/10 flex items-center justify-center mb-6" data-id="element-607">
                    <feature.icon className="w-6 h-6 text-viridia" data-id="element-608" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-609">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-610">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Scenario Cards */}
      <section className="py-20 bg-viridia-surface" data-id="element-611">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-612">
          <ScrollReveal data-id="element-613">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-614">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-615">
                Built for Real Scenarios
              </h2>
              <p className="text-gray-400" data-id="element-616">
                How our AI handles the situations that usually cost you money.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-617">
            <ScrollReveal data-id="element-618">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6" data-id="element-619">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-620">
                  Scenario 1: The After-Hours Yelp Lead
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-621">
                  A homeowner's AC breaks at 9 PM. They message 3 HVAC companies
                  on Yelp. You're asleep.
                </p>
                <div className="space-y-3 bg-viridia-surface rounded-xl p-4 border border-gray-800" data-id="element-622">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-623">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-624">
                    "Hi there! We offer 24/7 emergency AC repair. What seems to
                    be the issue with your unit?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-625">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-626" /> Lead captured while
                    competitors sleep
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.1} data-id="element-627">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6" data-id="element-628">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-629">
                  Scenario 2: The "On The Job" Missed Call
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-630">
                  You're under a sink fixing a leak. Your phone rings. You can't
                  answer it.
                </p>
                <div className="space-y-3 bg-viridia-surface rounded-xl p-4 border border-gray-800" data-id="element-631">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-632">
                    Viridia AI Response (Instant)
                  </div>
                  <div className="text-sm text-white" data-id="element-633">
                    "Hi, this is [Your Business]. Sorry we missed your call! Are
                    you looking to schedule a plumbing service?"
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-634">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-635" /> Stops them from
                    calling the next plumber
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-636">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6" data-id="element-637">
                <div className="text-sm text-viridia font-medium mb-4" data-id="element-638">
                  Scenario 3: The Tire Kicker
                </div>
                <p className="text-gray-300 text-sm mb-6" data-id="element-639">
                  Someone fills out a web form asking for a quote, but they
                  aren't ready to buy yet.
                </p>
                <div className="space-y-3 bg-viridia-surface rounded-xl p-4 border border-gray-800" data-id="element-640">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-641">
                    Viridia AI Nurture Sequence
                  </div>
                  <div className="text-sm text-white" data-id="element-642">
                    Automated follow-ups on Day 2, Day 5, and Day 14 with
                    helpful tips and a soft CTA.
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-800 text-xs text-viridia flex items-center gap-1" data-id="element-643">
                    <CheckCircleIcon className="w-3 h-3" data-id="element-644" /> Stays top-of-mind
                    without manual effort
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Proof / Story Section */}
      <section className="py-20 bg-viridia-black" data-id="element-645">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-646">
          <div className="bg-gradient-to-br from-viridia-surface to-viridia-black border border-gray-800 rounded-3xl p-8 md:p-12" data-id="element-647">
            <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-648">
              <ScrollReveal data-id="element-649">
                <h2 className="text-3xl font-bold text-white mb-6" data-id="element-650">
                  From Click to Booked Job in 3 Minutes
                </h2>
                <p className="text-gray-400 mb-6" data-id="element-651">
                  Here's exactly how our system worked for a local landscaping
                  client last week:
                </p>
                <ul className="space-y-4" data-id="element-652">
                  <li className="flex items-start gap-3" data-id="element-653">
                    <div className="w-6 h-6 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-viridia text-xs font-bold" data-id="element-654">
                      1
                    </div>
                    <span className="text-gray-300" data-id="element-655">
                      Lead clicked a Yelp Ad at 2:14 PM and sent a message
                      asking for a turf installation quote.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-656">
                    <div className="w-6 h-6 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-viridia text-xs font-bold" data-id="element-657">
                      2
                    </div>
                    <span className="text-gray-300" data-id="element-658">
                      AI replied at 2:14 PM asking about square footage and
                      timeline.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-659">
                    <div className="w-6 h-6 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-viridia text-xs font-bold" data-id="element-660">
                      3
                    </div>
                    <span className="text-gray-300" data-id="element-661">
                      Lead answered. AI offered an on-site estimate for Thursday
                      at 10 AM.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-662">
                    <div className="w-6 h-6 rounded-full bg-viridia/20 flex items-center justify-center flex-shrink-0 mt-0.5 text-viridia text-xs font-bold" data-id="element-663">
                      4
                    </div>
                    <span className="text-gray-300" data-id="element-664">
                      At 2:17 PM, the appointment was booked, added to the
                      owner's calendar, and logged in the CRM.
                    </span>
                  </li>
                </ul>
                <div className="mt-8" data-id="element-665">
                  <Link to="/case-studies" className="text-viridia hover:text-white font-medium flex items-center gap-2 transition-colors" data-id="element-666">
                    Read the full case study{' '}
                    <ArrowRightIcon className="w-4 h-4" data-id="element-667" />
                  </Link>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={0.2} data-id="element-668">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 shadow-2xl" data-id="element-669">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800" data-id="element-670">
                    <div className="font-medium text-white" data-id="element-671">
                      CRM Pipeline View
                    </div>
                    <div className="text-xs bg-viridia/20 text-viridia px-2 py-1 rounded" data-id="element-672">
                      Live Update
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-673">
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800 opacity-50" data-id="element-674">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-675">New Lead</div>
                      <div className="text-sm text-white" data-id="element-676">
                        John D. - Turf Quote
                      </div>
                    </div>
                    <div className="flex justify-center" data-id="element-677">
                      <ArrowRightIcon className="w-4 h-4 text-gray-600 rotate-90" data-id="element-678" />
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-viridia/50 shadow-[0_0_15px_rgba(16,185,129,0.1)]" data-id="element-679">
                      <div className="flex justify-between items-start mb-2" data-id="element-680">
                        <div className="text-xs text-viridia font-bold" data-id="element-681">
                          Estimate Booked
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-682">Just now</div>
                      </div>
                      <div className="text-sm text-white font-medium" data-id="element-683">
                        John D. - Turf Quote
                      </div>
                      <div className="text-xs text-gray-400 mt-1" data-id="element-684">
                        Thursday, 10:00 AM
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-685">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-686">
          <ScrollReveal data-id="element-687">
            <div className="text-center mb-12" data-id="element-688">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-689">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-400" data-id="element-690">
                Everything you need to know about our AI lead conversion system.
              </p>
            </div>
          </ScrollReveal>

          <div className="space-y-4" data-id="element-691">
            {faqs.map((faq, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-692">
                <details className="group bg-viridia-black border border-gray-800 rounded-xl overflow-hidden [&_summary::-webkit-details-marker]:hidden" data-id="element-693">
                  <summary className="flex items-center justify-between p-6 cursor-pointer text-white font-medium" data-id="element-694">
                    {faq.question}
                    <ChevronDownIcon className="w-5 h-5 text-gray-400 transition-transform group-open:-rotate-180" data-id="element-695" />
                  </summary>
                  <div className="px-6 pb-6 text-gray-400 text-sm leading-relaxed" data-id="element-696">
                    {faq.answer}
                  </div>
                </details>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-697">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-698">
          <ScrollReveal data-id="element-699">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-700">
              Ready to Stop Losing Leads?
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-701">
              Book a free strategy call to see how our AI can start converting
              your leads into booked jobs within 7 days.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-702">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-703">
                Book a Strategy Call
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia-surface border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-704">
                View Pricing
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}